import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Maximize2 } from "lucide-react";
import { api, buildWhatsAppLink, resolveImg } from "../lib/api";
import Lightbox from "../components/Lightbox";
import Seo from "../components/Seo";

const CATEGORY_LABEL = { musica: "Música", eventos: "Eventos", viajes: "Viajes", retratos: "Retratos" };

// Static animation configs — extracted out of render to avoid creating new objects every paint.
const IMG_INITIAL = { opacity: 0, y: 40 };
const IMG_IN_VIEW = { opacity: 1, y: 0 };
const IMG_VIEWPORT = { once: true, margin: "-80px" };
const IMG_TRANSITION = { duration: 0.9, ease: [0.2, 0.8, 0.2, 1] };

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    setProject(null);
    setError(false);
    api.get(`/projects/${slug}`)
      .then((r) => {
        setProject(r.data);
        return api.get(`/projects?category=${r.data.category}`);
      })
      .then((r) => r && setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 4)))
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="pt-40 max-w-3xl mx-auto px-6 text-center" data-testid="project-not-found">
        <p className="text-[11px] tracking-[0.4em] uppercase text-white/40 font-display">404</p>
        <h1 className="font-display uppercase text-5xl mt-4">Proyecto no encontrado</h1>
        <Link to="/" className="mt-10 inline-block link-reveal">Volver al inicio</Link>
      </div>
    );
  }
  if (!project) {
    return <div className="pt-40 px-6 text-white/40 text-sm tracking-widest uppercase" data-testid="loading">Cargando…</div>;
  }

  const accent = { musica: "var(--magenta)", eventos: "var(--tungsten)", viajes: "var(--cyan)", retratos: "var(--rose)" }[project.category] || "var(--magenta)";

  // SEO: build description from project data; ensure absolute image URL.
  const seoDescription = (project.description || project.intro_quote || `Serie fotográfica ${project.title} — ${CATEGORY_LABEL[project.category] || project.category} por Emmanuel Upegui.`).slice(0, 200);
  const coverUrl = project.cover && project.cover.startsWith("http") ? project.cover : resolveImg(project.cover);
  const projectJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ImageGallery",
        "name": project.title,
        "description": seoDescription,
        "url": `https://www.emmanuelupegui.com/proyecto/${project.slug}`,
        "image": coverUrl,
        "creator": { "@type": "Person", "name": "Emmanuel Upegui", "url": "https://www.emmanuelupegui.com/" },
        "genre": CATEGORY_LABEL[project.category] || project.category,
        ...(project.location ? { "contentLocation": { "@type": "Place", "name": project.location } } : {}),
        ...(project.year ? { "datePublished": String(project.year) } : {}),
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://www.emmanuelupegui.com/" },
          { "@type": "ListItem", "position": 2, "name": CATEGORY_LABEL[project.category] || project.category, "item": `https://www.emmanuelupegui.com/galeria/${project.category}` },
          { "@type": "ListItem", "position": 3, "name": project.title, "item": `https://www.emmanuelupegui.com/proyecto/${project.slug}` },
        ],
      },
    ],
  };

  return (
    <div data-testid={`project-page-${project.slug}`}>
      <Seo
        title={`${project.title} — ${CATEGORY_LABEL[project.category] || project.category}`}
        description={seoDescription}
        path={`/proyecto/${project.slug}`}
        image={coverUrl}
        type="article"
        keywords={`${project.title}, ${CATEGORY_LABEL[project.category] || project.category}, fotografía, Emmanuel Upegui${project.location ? `, ${project.location}` : ""}`}
        jsonLd={projectJsonLd}
      />
      {/* Hero cover */}
      <section className="relative h-[80svh] min-h-[520px] overflow-hidden mt-[5vh] bg-black">
        <img
          src={resolveImg(project.cover)}
          alt={project.title}
          className="absolute inset-0 w-full h-full object-cover kenburns"
          fetchPriority="high"
          style={{ objectPosition: `${project.cover_focus_x ?? 50}% ${project.cover_focus_y ?? 50}%` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/20 to-black/95" />
        <div className="absolute inset-0 max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] flex flex-col justify-end pb-12 sm:pb-20">
          <Link
            to={`/galeria/${project.category}`}
            data-testid="back-to-gallery"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase font-display text-white/70 hover:text-white"
          >
            <ArrowLeft size={14} /> Volver a {project.category}
          </Link>

          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}
            className="font-display uppercase font-bold tracking-tighter leading-[0.85] text-[12vw] sm:text-[9vw] mt-6"
          >
            {project.title}
          </motion.h1>

          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 text-[11px] tracking-[0.3em] uppercase font-display">
            <span style={{ color: accent }}>{project.category}</span>
            {project.location && <span className="text-white/60">{project.location}</span>}
            {project.year && <span className="text-white/40">{project.year}</span>}
            {project.client && <span className="text-white/40">CLIENTE · {project.client}</span>}
          </div>
        </div>
      </section>

      {/* INTRO QUOTE (optional pull-quote) */}
      {project.intro_quote && (
        <section className="max-w-[1200px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] py-20 sm:py-28">
          <p className="font-mono text-[10px] tracking-[0.4em]" style={{ color: accent }}>EPÍGRAFE</p>
          <blockquote className="mt-6 font-display uppercase font-medium tracking-tighter leading-[0.95] text-3xl sm:text-5xl lg:text-6xl text-white">
            "{project.intro_quote}"
          </blockquote>
        </section>
      )}

      {/* Description */}
      {project.description && (
        <section className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] py-12 sm:py-16 grid grid-cols-12 gap-8 border-t border-white/5">
          <div className="col-span-12 lg:col-span-4">
            <p className="text-[11px] tracking-[0.4em] uppercase font-display" style={{ color: accent }}>Sobre la serie</p>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <p className="font-serif-accent text-2xl sm:text-3xl leading-[1.3] text-white/85 font-light">
              {project.description}
            </p>
            {project.behance_url && (
              <a href={project.behance_url} target="_blank" rel="noreferrer"
                data-testid="behance-link"
                className="mt-10 inline-flex items-center gap-2 text-[12px] tracking-[0.3em] uppercase font-display border border-white/20 hover:border-white px-6 py-3"
              >
                Ver serie completa en Behance <ArrowUpRight size={14} />
              </a>
            )}
          </div>
        </section>
      )}

      {/* ESSAY (long form) */}
      {project.essay && (
        <section className="max-w-[820px] mx-auto px-6 sm:px-12 py-16 sm:py-24">
          <p className="font-mono text-[10px] tracking-[0.4em] mb-8" style={{ color: accent }}>ENSAYO</p>
          <div className="prose-essay text-white/85 text-[17px] sm:text-[18px] leading-[1.75] space-y-6 font-serif-accent font-light">
            {project.essay.split(/\n\s*\n/).map((paragraph, i) => (
              // Essay paragraphs are static text, no reordering — first 24 chars + index is a stable key.
              <p key={`${paragraph.slice(0, 24)}-${i}`}>{paragraph}</p>
            ))}
          </div>
        </section>
      )}

      {/* IMAGES STACK — full-width centered, click to open lightbox */}
      <section className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] pb-24 space-y-12 sm:space-y-16">
        {project.images.map((img, i) => (
          <motion.figure
            key={img.url || `img-${i}`}
            initial={IMG_INITIAL}
            whileInView={IMG_IN_VIEW}
            viewport={IMG_VIEWPORT}
            transition={IMG_TRANSITION}
            className="relative group"
          >
            <button
              onClick={() => setLightboxIdx(i)}
              data-testid={`open-lightbox-${i}`}
              className="block w-full p-0 m-0 relative overflow-hidden"
              aria-label={`Ampliar foto ${i + 1}`}
            >
              <img
                src={resolveImg(img.url)}
                alt={img.alt || `${project.title} ${i + 1}`}
                className="w-full h-auto block bg-[var(--surface)] transition-transform duration-700 group-hover:scale-[1.01]"
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
                fetchPriority={i === 0 ? "high" : "auto"}
              />
              <span className="absolute right-3 bottom-3 inline-flex items-center gap-2 bg-black/60 backdrop-blur-sm border border-white/10 px-3 py-1.5 text-white/80 opacity-0 group-hover:opacity-100 transition-opacity">
                <Maximize2 size={14} />
                <span className="font-mono text-[10px] tracking-[0.3em]">AMPLIAR</span>
              </span>
            </button>
            {(img.caption || img.technical) && (
              <figcaption className="mt-3 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                {img.caption && <p className="font-serif-accent text-base sm:text-lg text-white/70 italic font-light">{img.caption}</p>}
                {img.technical && <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/35">{img.technical}</p>}
              </figcaption>
            )}
            <span className="absolute top-2 left-2 font-mono text-[10px] tracking-[0.3em] text-white/30 bg-black/40 px-2 py-0.5 backdrop-blur-sm">
              {String(i + 1).padStart(2, "0")} / {String(project.images.length).padStart(2, "0")}
            </span>
          </motion.figure>
        ))}
      </section>

      {/* LIGHTBOX */}
      <Lightbox
        images={project.images}
        index={lightboxIdx}
        onClose={() => setLightboxIdx(null)}
        onNavigate={setLightboxIdx}
      />

      {/* CREDITS */}
      {(project.client || (project.artists?.length > 0) || project.credits) && (
        <section className="border-t border-white/5 py-16">
          <div className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-4">
              <p className="font-mono text-[10px] tracking-[0.4em]" style={{ color: accent }}>END CREDITS</p>
            </div>
            <div className="col-span-12 lg:col-span-8 space-y-6 font-mono text-[11px] tracking-[0.2em] text-white/70 leading-relaxed">
              {project.client && (
                <div>
                  <span className="block text-white/40 uppercase tracking-[0.4em] text-[10px] mb-1">CLIENTE</span>
                  <span className="text-white">{project.client}</span>
                </div>
              )}
              {project.artists?.length > 0 && (
                <div>
                  <span className="block text-white/40 uppercase tracking-[0.4em] text-[10px] mb-1">ARTISTAS</span>
                  <span className="text-white">{project.artists.join(" · ")}</span>
                </div>
              )}
              {project.credits && (
                <div>
                  <span className="block text-white/40 uppercase tracking-[0.4em] text-[10px] mb-1">CRÉDITOS</span>
                  <pre className="whitespace-pre-wrap text-white/80 font-mono">{project.credits}</pre>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="border-t border-white/5 py-16">
        <div className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] flex flex-col sm:flex-row justify-between gap-6 items-start sm:items-center">
          <p className="font-display uppercase tracking-tighter text-3xl sm:text-4xl">
            ¿Quieres una sesión <span style={{ color: accent }}>así</span>?
          </p>
          <a href={buildWhatsAppLink(`Hola Emmanuel, me interesa una serie como ${project.title}.`)}
            target="_blank" rel="noreferrer"
            data-testid="project-whatsapp-cta"
            className="border border-white/20 hover:border-[var(--whatsapp)] hover:text-[var(--whatsapp)] px-8 py-4 text-[12px] tracking-[0.3em] uppercase font-display"
          >
            Hablemos por WhatsApp
          </a>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] py-24">
          <p className="text-[11px] tracking-[0.4em] uppercase text-white/40 font-display">Más en {project.category}</p>
          <h2 className="font-display uppercase tracking-tighter text-4xl sm:text-5xl mt-3">Continúa mirando</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5">
            {related.map((p) => (
              <Link key={p.slug} to={`/proyecto/${p.slug}`} className="group block aspect-[3/4] overflow-hidden bg-[var(--surface)]" data-testid={`related-${p.slug}`}>
                <img
                  src={resolveImg(p.cover)}
                  alt={p.title}
                  className="w-full h-full object-cover img-hover"
                  style={{ objectPosition: `${p.cover_focus_x ?? 50}% ${p.cover_focus_y ?? 50}%` }}
                />
                <p className="mt-3 font-display uppercase text-sm tracking-wide group-hover:text-[var(--magenta)] transition-colors">{p.title}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
